import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { Switch } from "../../misc/Switch";
import { Button } from "../../misc/Button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { cn } from "../../../utils/classMerger";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";

const MIN_DAYS = 7;
const MAX_DAYS = 90;

interface CleanupSettings {
    myBrandEnabled: boolean;
    myBrandDays: number;
    leadGenEnabled: boolean;
    leadGenDays: number;
}

// Retention days are held as strings while editing so a partial entry ("1" on
// the way to "10") is not clamped up to the minimum on every keystroke.
interface CleanupDraft {
    myBrandEnabled: boolean;
    myBrandDays: string;
    leadGenEnabled: boolean;
    leadGenDays: string;
}

const clampDays = (value: number): number =>
    Math.max(MIN_DAYS, Math.min(MAX_DAYS, Math.round(value || MIN_DAYS)));

const toDraft = (settings: CleanupSettings): CleanupDraft => ({
    myBrandEnabled: settings.myBrandEnabled,
    myBrandDays: String(settings.myBrandDays),
    leadGenEnabled: settings.leadGenEnabled,
    leadGenDays: String(settings.leadGenDays),
});

const fromDraft = (draft: CleanupDraft): CleanupSettings => ({
    myBrandEnabled: draft.myBrandEnabled,
    myBrandDays: clampDays(Number(draft.myBrandDays)),
    leadGenEnabled: draft.leadGenEnabled,
    leadGenDays: clampDays(Number(draft.leadGenDays)),
});

const shortenedWindows = (
    next: CleanupSettings,
    saved: CleanupSettings
): string[] => {
    const changes: string[] = [];
    if (next.myBrandEnabled && next.myBrandDays < saved.myBrandDays) {
        changes.push(
            `My Brand ${saved.myBrandDays} → ${next.myBrandDays} days`
        );
    }
    if (next.leadGenEnabled && next.leadGenDays < saved.leadGenDays) {
        changes.push(
            `Lead Generation ${saved.leadGenDays} → ${next.leadGenDays} days`
        );
    }
    return changes;
};

// MB-1309: Content Cleanup settings. Old My Brand / Lead Generation posts
// are removed from the content calendar after the retention window (both ON
// by default at 30 days). Published posts on the social networks themselves
// are not affected.
const ContentCleanup = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [settings, setSettings] = useState<CleanupSettings | null>(null);
    const [draft, setDraft] = useState<CleanupDraft | null>(null);
    const [saving, setSaving] = useState(false);

    // Shortening a retention window deletes content on the next daily run and
    // cannot be undone, so it is confirmed before the save request goes out.
    const [pendingSave, setPendingSave] = useState<CleanupSettings | null>(
        null
    );

    const fetchSettings = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const response = await axios.get(
                `${backendUrl}/settings/content-cleanup`,
                { headers }
            );
            setSettings(response.data);
            setDraft(toDraft(response.data));
        } catch (error) {
            console.error("Failed to load content cleanup settings:", error);
            toast.error(
                <CustomToast message="Failed to load settings" type="error" />,
                { autoClose: 3000 }
            );
        }
    }, [backendUrl]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const save = async (update: CleanupSettings) => {
        try {
            setSaving(true);
            const headers = await getAuthHeader();
            const response = await axios.patch(
                `${backendUrl}/settings/content-cleanup`,
                update,
                { headers }
            );
            setSettings(response.data);
            setDraft(toDraft(response.data));
            toast.success(
                <CustomToast message="Settings saved" type="success" />,
                { autoClose: 3000 }
            );
        } catch (error) {
            console.error("Failed to update content cleanup settings:", error);
            toast.error(
                <CustomToast
                    message="Failed to update settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (!settings || !draft) return;
        const next = fromDraft(draft);
        if (shortenedWindows(next, settings).length > 0) {
            setPendingSave(next);
            return;
        }
        save(next);
    };

    const renderRow = (
        id: string,
        title: string,
        description: string,
        enabled: boolean,
        days: string,
        onToggle: (enabled: boolean) => void,
        onDaysChange: (days: string) => void
    ) => (
        <div className="border rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex flex-col flex-[0.8]">
                    <Label htmlFor={id} className="font-semibold text-primary">
                        {title}
                    </Label>
                    <span className="text-sm text-neutral-500 mt-1">
                        {description}
                    </span>
                </div>
                <Switch
                    id={id}
                    checked={enabled}
                    disabled={saving}
                    onClick={() => onToggle(!enabled)}
                />
            </div>
            <div
                className={cn(
                    "flex flex-wrap items-center gap-3",
                    !enabled && "opacity-50"
                )}
            >
                <Label htmlFor={`${id}-days`} className="text-sm text-primary">
                    Remove content older than
                </Label>
                <input
                    id={`${id}-days`}
                    type="number"
                    min={MIN_DAYS}
                    max={MAX_DAYS}
                    value={days}
                    disabled={saving || !enabled}
                    className="w-20 border rounded-md px-2 py-1 text-sm disabled:cursor-not-allowed"
                    onChange={(e) => onDaysChange(e.target.value)}
                    onBlur={() =>
                        onDaysChange(String(clampDays(Number(days))))
                    }
                />
                <span className="text-sm text-neutral-500">
                    days ({MIN_DAYS}–{MAX_DAYS})
                </span>
            </div>
        </div>
    );

    return (
        <Card
            className={`w-full max-w-4xl mx-auto ${
                isMobile ? "h-full m-0 rounded-none" : "h-auto"
            }`}
        >
            <div className="p-4">
                <Button
                    id="back-to-settings-btn"
                    onClick={() => navigate("/settings")}
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            <CardHeader className="mb-0 pb-0">
                <CardTitle>Content Cleanup</CardTitle>
                <CardDescription>
                    Automatically remove old My Brand and Lead Generation
                    content from your calendar. Posts already published to your
                    social accounts are never affected.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pb-8">
                {!settings || !draft ? (
                    <div className="w-full h-16 bg-gray-200 rounded animate-skeleton" />
                ) : (
                    <>
                        {renderRow(
                            "mybrand-cleanup",
                            "My Brand content",
                            "Old My Brand posts are removed from your content calendar after the retention window.",
                            draft.myBrandEnabled,
                            draft.myBrandDays,
                            (myBrandEnabled) =>
                                setDraft({ ...draft, myBrandEnabled }),
                            (myBrandDays) =>
                                setDraft({ ...draft, myBrandDays })
                        )}
                        {renderRow(
                            "leadgen-cleanup",
                            "Lead Generation content",
                            "Old Lead Generation posts and their generated images are removed after the retention window.",
                            draft.leadGenEnabled,
                            draft.leadGenDays,
                            (leadGenEnabled) =>
                                setDraft({ ...draft, leadGenEnabled }),
                            (leadGenDays) => setDraft({ ...draft, leadGenDays })
                        )}
                        <div className="bg-gray-100 p-3 rounded-md">
                            <p className="text-sm text-gray-600">
                                <strong className="text-primary">Note:</strong>{" "}
                                Cleanup runs daily. Turning a cleanup off keeps
                                all existing content; turning it back on
                                applies the retention window on the next run.
                            </p>
                        </div>
                        <div className="border-t border-gray-100 pt-6">
                            <Button
                                id="content-cleanup-save-btn"
                                onClick={handleSave}
                                disabled={saving}
                                className={cn(isMobile && "w-full")}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
            <ReconfirmOverlay
                isOpen={!!pendingSave}
                title="Shorten retention window?"
                message={
                    pendingSave && settings
                        ? `Shortening ${shortenedWindows(
                              pendingSave,
                              settings
                          ).join(
                              " and "
                          )}. Content older than the new window will be removed from your calendar on the next daily cleanup.`
                        : ""
                }
                messageTwo="This cannot be undone. Posts already published to your social accounts are not affected."
                confirmText="Shorten"
                onConfirm={() => {
                    if (pendingSave) save(pendingSave);
                    setPendingSave(null);
                }}
                onClose={() => setPendingSave(null)}
            />
        </Card>
    );
};

export default ContentCleanup;
